import type { SupabaseClient } from '@supabase/supabase-js'

export const ALLOWED_UPLOAD_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/avif',
  'image/heic',
  'image/heif',
])

export const ALLOWED_UPLOAD_EXTENSIONS = new Set([
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.avif',
  '.heic',
  '.heif',
])

export const BLOCKED_UPLOAD_EXTENSIONS = new Set([
  '.gif',
  '.mp4',
  '.mov',
  '.webm',
  '.m4v',
  '.avi',
  '.mkv',
])

type UnsupportedUploadReason = 'video' | 'gif' | 'unsupported'

const getFileExtension = (name: string): string => {
  const dotIndex = name.lastIndexOf('.')
  if (dotIndex === -1) return ''
  return name.slice(dotIndex).toLowerCase()
}

export function getUnsupportedUploadReason(file: File): UnsupportedUploadReason | null {
  const mimeType = (file.type || '').toLowerCase()
  const extension = getFileExtension(file.name)

  if (mimeType.startsWith('video/') || ['.mp4', '.mov', '.webm', '.m4v', '.avi', '.mkv'].includes(extension)) {
    return 'video'
  }

  if (mimeType === 'image/gif' || extension === '.gif') {
    return 'gif'
  }

  const isAllowedByMime = ALLOWED_UPLOAD_MIME_TYPES.has(mimeType)
  const isAllowedByExtension = ALLOWED_UPLOAD_EXTENSIONS.has(extension)

  if (isAllowedByMime || isAllowedByExtension) {
    return null
  }

  if (BLOCKED_UPLOAD_EXTENSIONS.has(extension)) {
    return extension === '.gif' ? 'gif' : 'video'
  }

  return 'unsupported'
}

export function isSupportedUploadImage(file: File): boolean {
  return getUnsupportedUploadReason(file) === null
}

export interface GeneratedIds {
  folderPath: string
  publicId: string
  fullPublicId: string
}

export function generatePublicId(albumSlug: string, albumId: string): GeneratedIds {
  const folderPath = `uploads/${albumId}`
  const randomUuid = (typeof crypto !== 'undefined' && (crypto as any).randomUUID)
    ? (crypto as any).randomUUID()
    : Math.random().toString(36).substring(2, 15)
  const publicId = `${albumSlug}_${randomUuid}`
  const fullPublicId = `${folderPath}/${publicId}`
  return { folderPath, publicId, fullPublicId }
}

export function buildCloudinaryFormData(params: {
  file: File
  folderPath: string
  userId: string
  publicId: string
  uploadPreset: string
}): FormData {
  const { file, folderPath, userId, publicId, uploadPreset } = params
  const formData = new FormData()
  formData.append('file', file)
  formData.append('upload_preset', uploadPreset)
  formData.append('folder', folderPath)
  formData.append('context', `uploaded_by=${userId}`)
  formData.append('public_id', publicId)
  return formData
}

export async function uploadToCloudinary(uploadUrl: string, formData: FormData): Promise<any> {
  const res = await fetch(uploadUrl, { method: 'POST', body: formData })
  const json = await res.json()
  if (!res.ok) {
    const msg = (json?.error?.message as string) || 'Cloudinary upload failed'
    throw new Error(msg)
  }
  return json
}

export async function insertInitialMediaRow(supabase: SupabaseClient, params: {
  albumId: string
  userId: string
  fullPublicId: string
  description?: string
}): Promise<void> {
  const { albumId, userId, fullPublicId, description } = params
  const { error } = await supabase
    .from('media')
    .insert({
      album_id: albumId,
      uploaded_by: userId,
      public_id: fullPublicId,
      created_at: new Date().toISOString(),
      description: description || null,
    } as any)
  if (error) throw error
}

export type PendingWatcher = {
  ensureReady: () => Promise<boolean>
  waitUntilFalse: (publicId: string) => Promise<void>
  cleanup: () => void
  isEnabled: () => boolean
}

// Generate a small local thumbnail (blob URL or data URL) to show previews fast
export async function generateLocalThumbnail(file: File, size = 120): Promise<string> {
  try {
    const hasCreateImageBitmap = typeof (window as any).createImageBitmap === 'function'
    if (hasCreateImageBitmap) {
      // Load the image first to know its intrinsic size
      const bitmap: any = await (window as any).createImageBitmap(file)

      // Draw into a square canvas using "cover" semantics to preserve aspect ratio
      const canvas = document.createElement('canvas')
      canvas.width = size
      canvas.height = size
      const ctx = canvas.getContext('2d')
      if (ctx) {
        const bw = (bitmap as any).width
        const bh = (bitmap as any).height
        // Scale to cover the square while keeping aspect ratio
        const scale = Math.max(size / bw, size / bh)
        const scaledW = bw * scale
        const scaledH = bh * scale
        const dx = (size - scaledW) / 2
        const dy = (size - scaledH) / 2
        ctx.drawImage(bitmap, dx, dy, scaledW, scaledH)
      }
      const blob: Blob | null = await new Promise((res) => canvas.toBlob((b) => res(b), 'image/webp', 0.8))
      if (blob) return URL.createObjectURL(blob)
      return canvas.toDataURL('image/jpeg', 0.8)
    }
  } catch (_) {
    // fall back below
  }
  // Fallback: show original (may be heavier on iOS, but guarantees a preview)
  return URL.createObjectURL(file)
}

export function createPendingWatcher(supabase: SupabaseClient, albumId?: string): PendingWatcher {
  let channel: any = null
  let enabled = true
  const resolvers = new Map<string, () => void>()

  const ensureReady = async () => {
    if (channel) return enabled
    channel = supabase
      .channel('public:media')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'media',
        filter: albumId ? `album_id=eq.${albumId}` : undefined,
      } as any, (payload: any) => {
        const row = payload?.new
        const pid = row?.public_id
        if (row && row.pending === false && pid) {
          const cb = resolvers.get(pid)
          if (cb) {
            cb()
            resolvers.delete(pid)
          }
        }
      })

    await new Promise<void>((resolve) => {
      let done = false
      const finish = (ok: boolean) => { if (done) return; done = true; enabled = ok; resolve() }
      channel.subscribe((status: string) => {
        if (status === 'SUBSCRIBED') finish(true)
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') finish(false)
      })
      setTimeout(() => finish(false), 3000)
    })
    return enabled
  }

  const waitUntilFalse = async (publicId: string) => {
    // Fast path
    try {
      const { data } = await supabase
        .from('media').select('public_id,pending')
        .eq('public_id', publicId)
        .maybeSingle()
      if (data && (data as any).pending === false) return
    } catch {}

    const ok = await ensureReady()
    if (!ok) {
      // Fallback polling by id
      const sleep = (ms: number) => new Promise(res => setTimeout(res, ms))
      for (;;) {
        try {
          const { data } = await supabase
            .from('media').select('public_id,pending')
            .eq('public_id', publicId)
            .maybeSingle()
          if (data && (data as any).pending === false) return
        } catch {}
        await sleep(1000)
      }
    }

    return new Promise<void>((resolve) => {
      resolvers.set(publicId, resolve)
      // Watchdog probe in case we miss the event
      setTimeout(async () => {
        try {
          const { data } = await supabase
            .from('media').select('public_id,pending')
            .eq('public_id', publicId)
            .maybeSingle()
          if (data && (data as any).pending === false) {
            const cb = resolvers.get(publicId)
            if (cb) cb()
          }
        } catch {}
      }, 2500)
    })
  }

  const cleanup = () => {
    try { if (channel) supabase.removeChannel(channel) } catch {}
    channel = null
    resolvers.clear()
  }

  return { ensureReady, waitUntilFalse, cleanup, isEnabled: () => enabled }
}
